import { ApolloError } from '@apollo/client';
import { ErrorResponse } from '@apollo/client/link/error';
import _ from 'lodash';

import { GraphQLError } from 'graphql';
import { safeToNumber } from '@tock/coretypes';
import PasscodePromptModal from 'components/modals/PasscodePromptModal';
import interactivePromise from '@tock/shared/utils/interactivePromise';

interface GQLErrorExtension {
  errorDetails?: {
    errorCode: number;
    message?: string;
  };
}

// All errors that should trigger a passcode prompt modal.
export enum PasscodePromptError {
  PASSCODE_REQUIRED = 3043,
  INCORRECT_PASSCODE = 3044,
}

/**
 * Spread this into an action's onError object if it can come back with a
 * passcode-related error. We don't want to show a Toast notification about
 * these errors because such a notification would be redundant alongside the
 * passcode prompt modal.
 */
export const handlePasscodeErrorsInModal = Object.values(PasscodePromptError).reduce(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (mapping: any, errorCode) => {
    mapping[errorCode] = _.noop;
    return mapping;
  },
  {}
);

const promptForPasscode = (errorMessage: string, errorCode: PasscodePromptError) => {
  return interactivePromise<string>((resolve, reject) => (
    <PasscodePromptModal
      onClose={reject}
      onPasscodeEntered={resolve}
      errorMessage={errorMessage}
      errorCode={errorCode}
    />
  ));
};

// used in cases where there multiple ways to handle the error based on error code
export const hasPassCodeErrorCode = (errorCode: number) => {
  return Object.values(PasscodePromptError).includes(errorCode);
};

/**
 * Use this as an action's .catch() callback to trigger a passcode prompt when a
 * relevant error code is returned. This will re-raise the error if it is not a
 * passcode-related error, so it is safe to chain to another .catch() when there
 * are other expected errors.
 *
 * @param {function} onPasscodeEntered Function that is called after a passcode is entered.
 * @param {function} onClose Function that is called after passcode prompt modal is closed.
 */
export const handlePasscodePromptError = (
  onPasscodeEntered: (passcode: string) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: (response: any) => void
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (response: any) => {
    if (response.error && response.error.errorCode) {
      const errorCode = safeToNumber(response.error.errorCode);
      if (hasPassCodeErrorCode(errorCode)) {
        promptForPasscode(response.error.message, errorCode).then(onPasscodeEntered).catch(onClose);
        return;
      }
    }
    throw response; // allows another .catch() to be chained onto the end of this
  };
};

/**
 * Checks a GraphQL error response for a passcode prompt, and captures a passcode if necessary
 * @param error The GraphQL error
 * @returns The passcode, if one was entered, `false` if the passcode prompt was cancelled, or `true` if no passcode was needed
 * @throws The original error if it was for something other than a passcode
 */
export const gqlHandlePasscodePromptError = async (error: ErrorResponse) => {
  const passcodeError = error?.graphQLErrors?.find((err: GraphQLError) => {
    const extensions = err.extensions as GQLErrorExtension;
    return hasPassCodeErrorCode(safeToNumber(extensions.errorDetails?.errorCode));
  });
  if (passcodeError) {
    const extensions = passcodeError.extensions as GQLErrorExtension;
    const errorCode = safeToNumber(extensions.errorDetails?.errorCode);
    try {
      return await promptForPasscode(
        extensions.errorDetails?.message ?? 'This operation requires a passcode',
        errorCode
      );
    } catch (_error) {
      return false;
    }
  }

  if (error) {
    throw new ApolloError({ ...error });
  }

  return true;
};
